import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {boolean} */
  #isPinchZoomed = false;

  /** @type {boolean} */
  #suppressClick = false;

  get #image() {
    return this.refs.image;
  }

  /** Current zoom scale (1 = no zoom). */
  get scale() {
    return this.#scale;
  }

  /** Whether the current zoom came from a trackpad/touch pinch. */
  get isPinchZoomed() {
    return this.#isPinchZoomed && this.#scale > MIN_ZOOM;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Trackpad pinch is reported as wheel + ctrlKey in Chromium/Firefox/Safari.
    this.addEventListener('wheel', this.#handleWheel, options);

    // Safari legacy trackpad pinch API.
    this.addEventListener('gesturestart', this.#handleGestureStart, options);
    this.addEventListener('gesturechange', this.#handleGestureChange, options);
    this.addEventListener('gestureend', this.#handleGestureEnd, options);

    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointermove', this.#handlePointerMove, options);
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('lostpointercapture', this.#handlePointerUp, { signal });
    this.addEventListener('click', this.#handleClick, { capture: true, signal });

    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Zoom toward a client point (used by desktop click-to-zoom).
   * @param {number} targetScale
   * @param {number} clientX
   * @param {number} clientY
   */
  zoomToward(targetScale, clientX, clientY) {
    const nextScale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);
    const oldScale = this.#scale;

    if (nextScale <= MIN_ZOOM) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
      this.#isPinchZoomed = false;
      this.#requestUpdateTransform();
      return;
    }

    this.#scale = nextScale;
    this.#hasManualZoom = false;
    this.#isPinchZoomed = false;

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2
    };
    const distanceFromCenter = {
      x: clientX - this.getBoundingClientRect().left - containerCenter.x,
      y: clientY - this.getBoundingClientRect().top - containerCenter.y
    };
    const ratio = this.#scale / oldScale;
    this.#translate.x = distanceFromCenter.x - (distanceFromCenter.x - this.#translate.x) * ratio;
    this.#translate.y = distanceFromCenter.y - (distanceFromCenter.y - this.#translate.y) * ratio;

    this.#requestUpdateTransform();
  }

  /** Public reset used by zoom-dialog when changing slides. */
  resetZoom() {
    this.#resetZoom();
  }

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    // Trackpad pinch zooms report ctrlKey.
    if (event.ctrlKey) {
      preventDefault(event);

      const oldScale = this.#scale;
      const zoomIntensity = event.deltaMode === 1 ? 0.05 : event.deltaMode === 2 ? 1 : 0.01;
      const zoomFactor = Math.exp(-event.deltaY * zoomIntensity);
      this.#scale = clamp(oldScale * zoomFactor, MIN_ZOOM, MAX_ZOOM);
      this.#hasManualZoom = this.#scale > MIN_ZOOM;
      this.#isPinchZoomed = this.#scale > MIN_ZOOM;

      const rect = this.getBoundingClientRect();
      const containerCenterX = this.clientWidth / 2;
      const containerCenterY = this.clientHeight / 2;
      const distanceFromCenterX = event.clientX - rect.left - containerCenterX;
      const distanceFromCenterY = event.clientY - rect.top - containerCenterY;
      const ratio = this.#scale / oldScale;
      this.#translate.x = distanceFromCenterX - (distanceFromCenterX - this.#translate.x) * ratio;
      this.#translate.y = distanceFromCenterY - (distanceFromCenterY - this.#translate.y) * ratio;

      this.#requestUpdateTransform();
      return;
    }

    // Two-finger trackpad scroll pans only after a real pinch zoom.
    // Click-to-zoom should keep scroll for the gallery and click-to-zoom-out.
    if (!this.#isPinchZoomed || this.#scale <= MIN_ZOOM) return;

    preventDefault(event);

    const deltaFactor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.clientHeight : 1;
    this.#translate.x -= event.deltaX * deltaFactor;
    this.#translate.y -= event.deltaY * deltaFactor;
    this.#requestUpdateTransform();
  };

  /**
   * @param {Event} event
   */
  #handleGestureStart = (event) => {
    preventDefault(event);
    this.#startScale = this.#scale;
  };

  /**
   * @param {Event} event
   */
  #handleGestureChange = (event) => {
    preventDefault(event);
    const gestureEvent = /** @type {Event & { scale?: number; clientX?: number; clientY?: number }} */ (event);
    const gestureScale = typeof gestureEvent.scale === 'number' ? gestureEvent.scale : 1;
    const oldScale = this.#scale;
    this.#scale = clamp(this.#startScale * gestureScale, MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = this.#scale > MIN_ZOOM;
    this.#isPinchZoomed = this.#scale > MIN_ZOOM;

    const rect = this.getBoundingClientRect();
    const clientX = typeof gestureEvent.clientX === 'number' ? gestureEvent.clientX : rect.left + rect.width / 2;
    const clientY = typeof gestureEvent.clientY === 'number' ? gestureEvent.clientY : rect.top + rect.height / 2;
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const ratio = this.#scale / oldScale;
    const distanceFromCenterX = clientX - rect.left - containerCenterX;
    const distanceFromCenterY = clientY - rect.top - containerCenterY;
    this.#translate.x = distanceFromCenterX - (distanceFromCenterX - this.#translate.x) * ratio;
    this.#translate.y = distanceFromCenterY - (distanceFromCenterY - this.#translate.y) * ratio;

    this.#requestUpdateTransform();
  };

  /**
   * @param {Event} event
   */
  #handleGestureEnd = (event) => {
    preventDefault(event);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (this.#scale <= MIN_ZOOM) return;
    if (event.button !== 0) return;

    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;

    // After trackpad pinch, start panning immediately so drag feels direct.
    if (this.#isPinchZoomed) {
      preventDefault(event);
      this.#hasDraggedBeyondThreshold = true;
      this.classList.add('is-panning');
      this.setPointerCapture(event.pointerId);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (!this.#isDragging || this.#scale <= MIN_ZOOM) return;

    const distance = getDistance(event, this.#startPosition);
    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) return;

    if (!this.#hasDraggedBeyondThreshold) {
      this.#hasDraggedBeyondThreshold = true;
      this.classList.add('is-panning');
      this.setPointerCapture(event.pointerId);
    }

    preventDefault(event);

    // Match pointer movement 1:1 on screen (transform applies scale after translate).
    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;
    this.#translate.x = this.#startTranslate.x + dx;
    this.#translate.y = this.#startTranslate.y + dy;
    this.#requestUpdateTransform();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    if (!this.#isDragging) return;

    if (this.#hasDraggedBeyondThreshold) {
      this.#suppressClick = true;
    }

    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.classList.remove('is-panning');
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.#suppressClick = false;
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (this.#scale > MIN_ZOOM) {
      preventDefault(event);
    }

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
      return;
    }

    if (this.#lastTapPosition) {
      const distance = getDistance(touch, this.#lastTapPosition);

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#handleDoubleTapFromTouch(touch);
        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);
    this.#startDragGestureFromTouch(touch);
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * @param {Touch} touch
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2
    };

    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#translate = { x: 0, y: 0 };
    } else {
      const tolerance = 0.05;

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        targetZoom = DEFAULT_ZOOM;
      } else {
        targetZoom = MIN_ZOOM;
      }
    }

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      const distanceFromCenter = {
        x: touch.clientX - containerCenter.x,
        y: touch.clientY - containerCenter.y
      };

      const ratio = this.#scale / oldScale;
      this.#translate.x = distanceFromCenter.x - (distanceFromCenter.x - this.#translate.x) * ratio;
      this.#translate.y = distanceFromCenter.y - (distanceFromCenter.y - this.#translate.y) * ratio;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount === 1 && this.#isDragging && this.#scale > MIN_ZOOM) {
      preventDefault(event);
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = this.#scale > MIN_ZOOM;
    this.#isPinchZoomed = this.#scale > MIN_ZOOM;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const ratio = this.#scale / oldScale;
    this.#translate.x = distanceFromCenterX - (distanceFromCenterX - this.#translate.x) * ratio;
    this.#translate.y = distanceFromCenterY - (distanceFromCenterY - this.#translate.y) * ratio;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx;
    this.#translate.y = this.#startTranslate.y + dy;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Constrain image translation to keep it within the viewport (object-fit: cover).
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth;
    let naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.height;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    } else {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;
    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);
    const maxTranslateX = horizontalOverflow / 2;
    const maxTranslateY = verticalOverflow / 2;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  #syncZoomedClass() {
    this.classList.toggle('is-zoomed', this.#scale > MIN_ZOOM);
    this.classList.toggle('is-pinch-zoomed', this.#scale > MIN_ZOOM && this.#isPinchZoomed);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.#syncZoomedClass();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  #resetZoom = () => {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.#isPinchZoomed = false;
    this.classList.remove('is-panning');

    this.#syncZoomedClass();
    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * @param {Point | Touch | PointerEvent} point1
 * @param {Point | Touch | PointerEvent} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const x1 =
    /** @type {Point} */ (point1).x ??
    /** @type {Touch} */ (point1).clientX ??
    /** @type {PointerEvent} */ (point1).clientX;
  const y1 =
    /** @type {Point} */ (point1).y ??
    /** @type {Touch} */ (point1).clientY ??
    /** @type {PointerEvent} */ (point1).clientY;
  const x2 =
    /** @type {Point} */ (point2).x ??
    /** @type {Touch} */ (point2).clientX ??
    /** @type {PointerEvent} */ (point2).clientX;
  const y2 =
    /** @type {Point} */ (point2).y ??
    /** @type {Touch} */ (point2).clientY ??
    /** @type {PointerEvent} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */
